/** Left road section of the LUDOKA game board - Crypt skin */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { LEFT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for left section (Yellow)
 * Ordered left to right for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'YS1',
  'YC1',
  'YF1',
  'YS2',
  'YC2',
  'YF2',
  'YS3',
  'YC3',
  'YF3',
  'YS4',
  'YC4',
  'YF4',
  'YS5',
  'YC5',
  'YF5',
  'YS6',
  'YC6',
  'YF6',
  'YFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function CryptLeftRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Left road section with yellow final stretch"
    >
      {/* All crypt visual content shifted +4.11 in Y to align with basics coordinate space */}
      <g transform="translate(0, 4.11)">
        {/* Top left corner - gray placeholder */}
        <path
          d="M21.3999 7.62H88.2399V84.46H11.3999V17.62C11.3999 12.1 15.8799 7.62 21.3999 7.62Z"
          fill="#C4C4C4"
        />

        {/* Column 1: Dark brown top square */}
        <path
          d="M165.08 7.62H88.2402V84.46H165.08V7.62Z"
          fill="#3D351F"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 1 center path - middle row */}
        <path
          d="M150.69 90.8H102.64C98.1887 90.8 94.5801 94.4086 94.5801 98.86V146.91C94.5801 151.361 98.1887 154.97 102.64 154.97H150.69C155.141 154.97 158.75 151.361 158.75 146.91V98.86C158.75 94.4086 155.141 90.8 150.69 90.8Z"
          fill="white"
        />

        {/* Left column center row - light gray */}
        <path d="M88.2399 84.46H11.3999V161.3H88.2399V84.46Z" fill="#EFEFEF" />

        {/* Column 1: Yellow gradient center row */}
        <path
          d="M165.08 84.46H88.2402V161.3H165.08V84.46Z"
          fill="url(#crpL_paint0)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 1 center path - middle row (second instance for layering) */}
        <path
          d="M150.69 90.8H102.64C98.1887 90.8 94.5801 94.4086 94.5801 98.86V146.91C94.5801 151.361 98.1887 154.97 102.64 154.97H150.69C155.141 154.97 158.75 151.361 158.75 146.91V98.86C158.75 94.4086 155.141 90.8 150.69 90.8Z"
          fill="white"
        />
        {/* Column 1 center path - top row */}
        <path
          d="M150.69 13.95H102.64C98.1887 13.95 94.5801 17.5586 94.5801 22.01V70.06C94.5801 74.5114 98.1887 78.12 102.64 78.12H150.69C155.141 78.12 158.75 74.5114 158.75 70.06V22.01C158.75 17.5586 155.141 13.95 150.69 13.95Z"
          fill="white"
        />

        {/* Column 2: Dark brown center row */}
        <path
          d="M241.92 84.46H165.08V161.3H241.92V84.46Z"
          fill="#3D351F"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 center path */}
        <path
          d="M227.53 90.8H179.48C175.029 90.8 171.42 94.4086 171.42 98.86V146.91C171.42 151.361 175.029 154.97 179.48 154.97H227.53C231.981 154.97 235.59 151.361 235.59 146.91V98.86C235.59 94.4086 231.981 90.8 227.53 90.8Z"
          fill="white"
        />

        {/* Column 3: Yellow gradient center row */}
        <path
          d="M318.76 84.46H241.92V161.3H318.76V84.46Z"
          fill="url(#crpL_paint1)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 3 center path */}
        <path
          d="M304.37 90.8H256.32C251.868 90.8 248.26 94.4086 248.26 98.86V146.91C248.26 151.361 251.868 154.97 256.32 154.97H304.37C308.821 154.97 312.43 151.361 312.43 146.91V98.86C312.43 94.4086 308.821 90.8 304.37 90.8Z"
          fill="white"
        />

        {/* Column 4: Dark brown center row */}
        <path
          d="M395.61 84.46H318.77V161.3H395.61V84.46Z"
          fill="#3D351F"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 center path */}
        <path
          d="M381.21 90.8H333.16C328.709 90.8 325.1 94.4086 325.1 98.86V146.91C325.1 151.361 328.709 154.97 333.16 154.97H381.21C385.661 154.97 389.27 151.361 389.27 146.91V98.86C389.27 94.4086 385.661 90.8 381.21 90.8Z"
          fill="white"
        />

        {/* Column 5: Yellow gradient center row */}
        <path
          d="M472.45 84.46H395.61V161.3H472.45V84.46Z"
          fill="url(#crpL_paint2)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Grass green ground border - base layer */}
        <path
          d="M11.4399 224.85V250.43C11.8199 254.89 13.1399 260.9 17.8099 264.72C24.4199 270.12 34.0699 268.13 41.0999 267.11C59.6599 264.41 52.5099 269.6 95.6999 269.37C123.48 269.22 122.91 267.1 148.24 267.04C171.11 266.99 172.28 268.72 196.38 269.35C225.57 270.12 226.97 267.66 268.84 266.83C299.03 266.23 297.86 267.5 330.95 266.83C361.24 266.22 359.75 265.22 388.85 264.81C401.74 264.63 405.67 264.77 448.44 264.81C462.27 264.82 464.87 264.81 472.32 264.81L472.44 237.42L11.4399 224.85Z"
          fill="url(#crpL_paint3)"
        />

        {/* Yellow gradient ground overlay */}
        <path
          d="M472.39 225V252.39C464.95 252.39 462.27 252.4 448.45 252.39H446.97C443.72 252.38 440.69 252.38 437.88 252.37C436.1 252.37 434.39 252.37 432.77 252.37C428.55 252.37 424.86 252.35 421.6 252.35C409.11 252.32 402.91 252.28 397.34 252.31C394.86 252.32 392.51 252.34 389.79 252.38C389.48 252.38 389.17 252.39 388.85 252.39C387.88 252.4 386.94 252.42 386.03 252.43C382.64 252.49 379.69 252.56 377.07 252.62C359.43 253.11 356.44 253.89 330.95 254.4C318.24 254.66 310.59 254.63 304.27 254.53C301.99 254.5 299.88 254.45 297.77 254.41C295.8 254.37 293.83 254.33 291.71 254.3C289.85 254.27 287.86 254.25 285.67 254.25C282.64 254.23 279.24 254.25 275.16 254.3C274.93 254.3 274.7 254.3 274.46 254.3C272.71 254.32 270.84 254.35 268.84 254.39C268.66 254.39 268.48 254.39 268.3 254.39C267.43 254.41 266.57 254.43 265.73 254.45C226.88 255.34 224.82 257.65 196.38 256.91C183.24 256.56 176.92 255.89 170.18 255.36C166.58 255.08 162.86 254.83 157.92 254.7C157.67 254.69 157.42 254.69 157.17 254.68C154.58 254.61 151.65 254.59 148.24 254.6C145.85 254.6 143.69 254.62 141.72 254.66C126.8 254.95 122.44 256.11 109 256.65H108.98C106.06 256.76 102.7 256.86 98.7199 256.9C97.8199 256.92 96.8899 256.92 95.9299 256.93H95.6999C67.2599 257.07 60.6499 254.88 54.1599 254.13C52.0099 253.89 49.8799 253.8 46.9699 254.01C45.6899 254.09 44.2599 254.23 42.6099 254.45C42.1299 254.51 41.6199 254.58 41.0999 254.66C38.5599 255.03 35.6899 255.53 32.7399 255.75C27.5099 256.16 22.0399 255.73 17.8099 252.28C14.5199 249.59 12.8899 245.81 12.0799 242.25C11.7399 240.76 11.5499 239.31 11.4399 237.99V212.41L201.65 217.59L241.45 218.67L306.1 220.43L328.64 221.05H328.65L362.57 221.97H362.79H362.8L429.13 223.77L466.88 224.8L472.39 224.97V225Z"
          fill="url(#crpL_paint4)"
        />

        {/* Column 6: White center path squares */}
        <path
          d="M458.05 90.8H410C405.549 90.8 401.94 94.4086 401.94 98.86V146.91C401.94 151.361 405.549 154.97 410 154.97H458.05C462.501 154.97 466.11 151.361 466.11 146.91V98.86C466.11 94.4086 462.501 90.8 458.05 90.8Z"
          fill="white"
        />
        <path
          d="M458.05 90.8H410C405.549 90.8 401.94 94.4086 401.94 98.86V146.91C401.94 151.361 405.549 154.97 410 154.97H458.05C462.501 154.97 466.11 151.361 466.11 146.91V98.86C466.11 94.4086 462.501 90.8 458.05 90.8Z"
          fill="white"
        />

        {/* Top row: alternating dark/light gold squares */}
        {/* Column 3 top - dark gold */}
        <path
          d="M318.76 7.62H241.92V84.46H318.76V7.62Z"
          fill="url(#crpL_paint5)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 5 top - dark gold */}
        <path
          d="M472.45 7.62H395.61V84.46H472.45V7.62Z"
          fill="url(#crpL_paint6)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 top - light gold */}
        <path
          d="M241.92 7.62H165.08V84.46H241.92V7.62Z"
          fill="url(#crpL_paint7)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 top - light gold */}
        <path
          d="M395.61 7.62H318.77V84.46H395.61V7.62Z"
          fill="url(#crpL_paint8)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Bottom left corner - gray placeholder */}
        <path
          d="M11.3999 161.3H88.2399V238.14H21.3999C15.8799 238.14 11.3999 233.66 11.3999 228.14V161.3Z"
          fill="#C4C4C4"
        />

        {/* Bottom row: alternating gold squares */}
        {/* Column 1 bottom - dark gold */}
        <path
          d="M165.08 161.3H88.2402V238.14H165.08V161.3Z"
          fill="url(#crpL_paint9)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Bottom left corner - light gold */}
        <path
          d="M11.3999 161.3H88.2399V238.14H21.3999C15.8799 238.14 11.3999 233.66 11.3999 228.14V161.3Z"
          fill="url(#crpL_paint10)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Top left corner - light gold */}
        <path
          d="M21.3999 7.62H88.2399V84.46H11.3999V17.62C11.3999 12.1 15.8799 7.62 21.3999 7.62Z"
          fill="url(#crpL_paint11)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 1 middle - dark gold */}
        <path
          d="M88.2399 84.46H11.3999V161.3H88.2399V84.46Z"
          fill="url(#crpL_paint12)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 3 bottom - dark gold */}
        <path
          d="M318.76 161.3H241.92V238.14H318.76V161.3Z"
          fill="url(#crpL_paint13)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 5 bottom - dark gold */}
        <path
          d="M472.45 161.3H395.61V238.14H472.45V161.3Z"
          fill="url(#crpL_paint14)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 bottom - light gold */}
        <path
          d="M241.92 161.3H165.08V238.14H241.92V161.3Z"
          fill="url(#crpL_paint15)"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 bottom - light gold */}
        <path
          d="M395.61 161.3H318.77V238.14H395.61V161.3Z"
          fill="url(#crpL_paint16)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Star decoration - shadow layer + white top layer */}
        <path
          d="M212.04 190.98L205.42 179.03C204.78 177.88 203.14 177.83 202.44 178.95L195.21 190.54L181.8 193.14C180.5 193.39 179.96 194.94 180.8 195.95L189.59 206.41L187.92 219.97C187.76 221.28 189.06 222.28 190.29 221.79L202.95 216.66L215.33 222.44C216.53 223 217.88 222.07 217.79 220.75L216.82 207.12L226.14 197.13C227.04 196.16 226.57 194.59 225.29 194.27L212.03 190.98H212.04Z"
          fill="#303030"
        />
        {/* White top layer */}
        <path
          d="M212.04 189.68L205.42 177.73C204.78 176.58 203.14 176.53 202.44 177.65L195.21 189.24L181.8 191.84C180.5 192.09 179.96 193.64 180.8 194.65L189.59 205.11L187.92 218.67C187.76 219.98 189.06 220.98 190.29 220.49L202.95 215.36L215.33 221.14C216.53 221.7 217.88 220.77 217.79 219.45L216.82 205.82L226.14 195.83C227.04 194.86 226.57 193.29 225.29 192.97L212.03 189.68H212.04Z"
          fill="white"
        />

        {/* Yellow gradient triangle/arrow on right */}
        <path
          d="M587.71 122.88L472.45 238.14L473.18 7.62L587.71 122.88Z"
          fill="url(#crpL_paint17)"
          stroke="black"
          strokeMiterlimit="10"
        />

        <defs>
          {/* paint0: Yellow gradient for column 1 center row */}
          <linearGradient
            id="crpL_paint0"
            x1="88.2402"
            y1="122.88"
            x2="165.08"
            y2="122.88"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBB03B" />
            <stop offset="0.24" stopColor="#FCEE21" />
            <stop offset="0.47" stopColor="#F15A24" />
            <stop offset="0.75" stopColor="#FCEE21" />
            <stop offset="1" stopColor="#F15A24" />
          </linearGradient>

          {/* paint1: Yellow gradient for column 3 center row */}
          <linearGradient
            id="crpL_paint1"
            x1="241.92"
            y1="1.93657e-05"
            x2="318.77"
            y2="1.93657e-05"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBB03B" />
            <stop offset="0.24" stopColor="#FCEE21" />
            <stop offset="0.47" stopColor="#F15A24" />
            <stop offset="0.75" stopColor="#FCEE21" />
            <stop offset="1" stopColor="#F15A24" />
          </linearGradient>

          {/* paint2: Yellow gradient for column 5 center row */}
          <linearGradient
            id="crpL_paint2"
            x1="395.61"
            y1="1.69921e-05"
            x2="472.45"
            y2="1.69921e-05"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBB03B" />
            <stop offset="0.24" stopColor="#FCEE21" />
            <stop offset="0.47" stopColor="#F15A24" />
            <stop offset="0.75" stopColor="#FCEE21" />
            <stop offset="1" stopColor="#F15A24" />
          </linearGradient>

          {/* paint3: Grass green gradient for ground base */}
          <linearGradient
            id="crpL_paint3"
            x1="241.94"
            y1="253.19"
            x2="241.94"
            y2="279.87"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2F3806" />
            <stop offset="1" stopColor="#393D04" />
          </linearGradient>

          {/* paint4: Yellow gradient for ground overlay */}
          <linearGradient
            id="crpL_paint4"
            x1="10.3599"
            y1="234.75"
            x2="472.17"
            y2="234.75"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBB03B" />
            <stop offset="0.24" stopColor="#FCEE21" />
            <stop offset="0.47" stopColor="#F15A24" />
            <stop offset="0.75" stopColor="#FCEE21" />
            <stop offset="1" stopColor="#F15A24" />
          </linearGradient>

          {/* paint5: Dark gold for column 3 top */}
          <linearGradient
            id="crpL_paint5"
            x1="241.92"
            y1="46.04"
            x2="318.77"
            y2="46.04"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint6: Dark gold for column 5 top */}
          <linearGradient
            id="crpL_paint6"
            x1="395.61"
            y1="-5.13954e-06"
            x2="472.45"
            y2="-5.13954e-06"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint7: Light gold for column 2 top */}
          <linearGradient
            id="crpL_paint7"
            x1="165.08"
            y1="46.04"
            x2="241.92"
            y2="46.04"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint8: Light gold for column 4 top */}
          <linearGradient
            id="crpL_paint8"
            x1="318.77"
            y1="-5.13954e-06"
            x2="395.61"
            y2="-5.13954e-06"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint9: Dark gold for column 1 bottom */}
          <linearGradient
            id="crpL_paint9"
            x1="88.2402"
            y1="199.72"
            x2="165.08"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint10: Light gold for bottom-left corner */}
          <linearGradient
            id="crpL_paint10"
            x1="11.3999"
            y1="199.72"
            x2="88.2399"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint11: Light gold for top-left corner */}
          <linearGradient
            id="crpL_paint11"
            x1="11.3999"
            y1="46.04"
            x2="88.2399"
            y2="46.04"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint12: Dark gold for column 1 middle */}
          <linearGradient
            id="crpL_paint12"
            x1="11.3999"
            y1="122.88"
            x2="88.2399"
            y2="122.88"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint13: Dark gold for column 3 bottom */}
          <linearGradient
            id="crpL_paint13"
            x1="-0.000124334"
            y1="199.72"
            x2="0.999876"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint14: Dark gold for column 5 bottom */}
          <linearGradient
            id="crpL_paint14"
            x1="395.61"
            y1="199.72"
            x2="472.45"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4C4018" />
            <stop offset="1" stopColor="#443427" />
          </linearGradient>

          {/* paint15: Light gold for column 2 bottom */}
          <linearGradient
            id="crpL_paint15"
            x1="7.62939e-05"
            y1="199.72"
            x2="1.00008"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint16: Light gold for column 4 bottom */}
          <linearGradient
            id="crpL_paint16"
            x1="318.77"
            y1="199.72"
            x2="395.61"
            y2="199.72"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#876C3D" />
            <stop offset="1" stopColor="#947636" />
          </linearGradient>

          {/* paint17: Yellow gradient for right triangle/arrow */}
          <linearGradient
            id="crpL_paint17"
            x1="472.45"
            y1="122.88"
            x2="877.49"
            y2="122.88"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBB03B" />
            <stop offset="0.24" stopColor="#FCEE21" />
            <stop offset="0.47" stopColor="#F15A24" />
            <stop offset="0.75" stopColor="#FCEE21" />
            <stop offset="1" stopColor="#F15A24" />
          </linearGradient>
        </defs>
      </g>

      {/* Pawn rendering - OUTSIDE the translated group, in basics coordinate space */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents
      )}
    </svg>
  );
}
